import { isJsonBoolean, isJsonObject, type JsonValue } from '@/api/json';
import type { BadgeVariant, MenuItem } from '@/data/menu';
import menu from '@/data/menu';
import { can } from '@/composables/usePermissions';
import { defineStore } from 'pinia';

interface MenuState {
    menu: MenuItem[];
    sectionCollapseState: Record<string, boolean>;
}

/** Items with no `permission` are visible to everyone. */
function isHidden(item: MenuItem): boolean {
    return item.permission !== undefined && !can(item.permission);
}

/**
 * A heading whose every item has been hidden goes too -- an empty section
 * header is worse than no section at all. Only headings collapse: an ordinary
 * item with children (Warehouse Transfers, say) navigates somewhere itself, so
 * it survives losing them.
 */
function collapsesWhenEmptied(item: MenuItem, remaining: number): boolean {
    return remaining === 0 && item.heading === true;
}

/** The item as the user should see it, or null if they should not see it. */
function visibleItem(item: MenuItem): MenuItem | null {
    if (isHidden(item)) {
        return null;
    }

    if (!item.sub) {
        return item;
    }

    const sub = filterByPermission(item.sub);

    return collapsesWhenEmptied(item, sub.length) ? null : { ...item, sub };
}

function filterByPermission(items: MenuItem[]): MenuItem[] {
    return items.map(visibleItem).filter((item): item is MenuItem => item !== null);
}

function loadCollapseState(): Record<string, boolean> {
    try {
        const parsed: JsonValue = JSON.parse(localStorage.getItem('menuCollapseState') ?? '{}');

        if (!isJsonObject(parsed)) {
            return {};
        }

        // SAFETY: only the boolean entries survive the filter above.
        return Object.fromEntries(Object.entries(parsed).filter(([, value]) => isJsonBoolean(value))) as Record<
            string,
            boolean
        >;
    } catch {
        return {};
    }
}

function findMenu(m: MenuItem[], badge: string): MenuItem | undefined {
    for (const item of m) {
        if (item.name === badge) {
            return item;
        }
        if (item.sub) {
            const found = findMenu(item.sub, badge);
            if (found) {
                return found;
            }
        }
    }
    return undefined;
}

function findParentMenu(m: MenuItem[], targetName: string): MenuItem | undefined {
    for (const item of m) {
        if (item.sub?.some((subItem: MenuItem) => subItem.name === targetName)) {
            return item;
        }
        if (item.sub) {
            const found = findParentMenu(item.sub, targetName);
            if (found) {
                return found;
            }
        }
    }
    return undefined;
}

export const useMenuStore = defineStore('menu', {
    state: (): MenuState => {
        return {
            menu: menu.main ?? [],
            sectionCollapseState: loadCollapseState(),
        };
    },
    getters: {
        visibleMenu(state): MenuItem[] {
            return filterByPermission(state.menu);
        },

        isSectionCollapsed(state): (name: string) => boolean {
            return (name: string) => state.sectionCollapseState[name] ?? false;
        },
    },
    actions: {
        toggleSection(name: string) {
            this.sectionCollapseState[name] = !(this.sectionCollapseState[name] ?? false);
            localStorage.setItem('menuCollapseState', JSON.stringify(this.sectionCollapseState));
        },

        addBadge(badge: string, count: number, variant: BadgeVariant = 'primary') {
            const m = findMenu(this.menu, badge);

            if (!m) {
                return;
            }

            m.badge = count.toString();
            m['badge-variant'] = variant;

            // Find and open parent menu if this is a sub-item
            const parent = findParentMenu(this.menu, badge);
            if (parent) {
                parent.open = true;
            }
        },

        removeBadge(badge: string) {
            const m = findMenu(this.menu, badge);

            if (!m) {
                return;
            }

            delete m.badge;
            delete m['badge-variant'];
        },
    },
});
